import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantRawMaterialCategoryService from '../../services/restaurant/RestaurantRawMaterialCategoryService';
import {
  closeRawMaterialCategory,
  openRawMaterialCategoryCreate,
  openRawMaterialCategoryEdit,
  setRawMaterialCategoryLoading,
  setRawMaterialCategoryPage,
  setRawMaterialCategorySaving,
  updateRawMaterialCategoryField,
} from '../../store/slices/restaurant/restaurantRawMaterialCategorySlice';
import { AppDispatch, RootState } from '../../store/store';
import {
  RestaurantRawMaterialCategoryDTO,
  RestaurantRawMaterialCategoryFilters,
  RestaurantRawMaterialCategoryForm,
} from '../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantPageRequest } from '../../types/restaurant/RestaurantRecipeTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantRawMaterialCategories = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector(
    (root: RootState) => root.restaurantRawMaterialCategory
  );
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);

  const load = async (
    request: RestaurantPageRequest<RestaurantRawMaterialCategoryFilters>
  ) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    dispatch(setRawMaterialCategoryLoading(true));
    try {
      const page = await RestaurantRawMaterialCategoryService.listFiltered(
        branchId,
        request
      );
      if (currentRequest !== requestId.current) return;
      dispatch(setRawMaterialCategoryPage(page));
      return page;
    } catch (error) {
      if (currentRequest === requestId.current) handle(error);
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(setRawMaterialCategoryLoading(false));
      }
    }
  };

  const edit = async (item: RestaurantRawMaterialCategoryDTO) => {
    try {
      dispatch(
        openRawMaterialCategoryEdit(
          (await RestaurantRawMaterialCategoryService.get(item.id)).data
        )
      );
    } catch (error) {
      handle(error);
    }
  };

  const save = async (
    request: RestaurantPageRequest<RestaurantRawMaterialCategoryFilters>,
    onEmptyPage: () => void
  ) => {
    if (!branchId || state.saving) return;
    dispatch(setRawMaterialCategorySaving(true));
    try {
      const response = state.form.id
        ? await RestaurantRawMaterialCategoryService.update(
            state.form.id,
            state.form
          )
        : await RestaurantRawMaterialCategoryService.create(
            branchId,
            state.form
          );
      show(
        response.type || 'success',
        response.title || 'Categoría guardada',
        response.message || 'La categoría se guardó correctamente.'
      );
      dispatch(closeRawMaterialCategory());
      const page = await load(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRawMaterialCategorySaving(false));
    }
  };

  const toggleActive = (
    item: RestaurantRawMaterialCategoryDTO,
    request: RestaurantPageRequest<RestaurantRawMaterialCategoryFilters>,
    onEmptyPage: () => void
  ) => {
    const execute = async () => {
      try {
        const response =
          await RestaurantRawMaterialCategoryService.updateActive(
            item.id,
            !item.active
          );
        show(
          response.type || 'success',
          response.title || 'Categoría actualizada',
          response.message || 'Estado actualizado.'
        );
        const page = await load(request);
        if (page?.content.length === 0 && request.page > 0) onEmptyPage();
      } catch (error) {
        handle(error);
      }
    };
    if (!item.active) return execute();
    confirm({
      title: 'Desactivar categoría',
      message: `¿Desea desactivar “${item.name}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  const updateField = <K extends keyof RestaurantRawMaterialCategoryForm>(
    field: K,
    value: RestaurantRawMaterialCategoryForm[K]
  ) => dispatch(updateRawMaterialCategoryField({ field, value }));

  return {
    ...state,
    load,
    edit,
    save,
    toggleActive,
    updateField,
    openCreate: () => dispatch(openRawMaterialCategoryCreate()),
    closeModal: () => dispatch(closeRawMaterialCategory()),
  };
};

export default useRestaurantRawMaterialCategories;
